import { formatEther } from 'viem';
import type { MintSchedule } from '@soundxyz/sdk';
import { getScheduleId } from '@components/Song/MintComponents/helpers';
import type { ContractType, EditionCollectableSchedulesInfoFragment } from '@graphql/reactQuery';
import { useEditionMintSchedules } from '@store/sdk/reactQuery';
import { SoundContractInterfaceIds } from '@utils/constants';

export const ALLOWED_INTERFACES_FOR_CC = {
  /** use the minter adapter */
  IRangeEditionMinter: SoundContractInterfaceIds.IRangeEditionMinter,
  /** Native Support */
  IRangeEditionMinterV2: SoundContractInterfaceIds.IRangeEditionMinterV2,
  /** Native Support */
  IRangeEditionMinterV2_1: SoundContractInterfaceIds.IRangeEditionMinterV2_1,
  /** Native Support */
  IMerkleDropMinterV2: SoundContractInterfaceIds.IMerkleDropMinterV2,
  /** Native Support */
  IMerkleDropMinterV2_1: SoundContractInterfaceIds.IMerkleDropMinterV2_1,
};

export function useIsCreditCardOnEditionAllowed({
  releaseContractAddress,
  scheduleIds,
  schedule,
  chainId,
  quantity,
  auctionContractType,
}: {
  releaseContractAddress: string;
  scheduleIds: EditionCollectableSchedulesInfoFragment['scheduleIds'] | null | undefined;
  schedule: MintSchedule;
  chainId: number;
  quantity: number;
  auctionContractType: ContractType | null;
}) {
  // TODO: FIX https://linear.app/sound/issue/ENG-8133/implement-credit-card-purchase-with-tiers-on-webapp
  const mintSchedulesInfo = useEditionMintSchedules({
    editionAddress: releaseContractAddress,
    scheduleIds,
    chainId,
    auctionContractType,
  });

  const activeMintSchedule = mintSchedulesInfo.data?.mintSchedules.find(
    mintSchedule => getScheduleId(mintSchedule) === getScheduleId(schedule),
  );

  if (!activeMintSchedule) {
    return {
      totalPriceEth: '0',
      isAllowed: false,
      activeMintSchedule: null,
    };
  }

  const totalPrice =
    activeMintSchedule.price * BigInt(quantity) + activeMintSchedule.platformTransactionFee;
  const totalPriceEth = formatEther(totalPrice);
  const isAllowed = Object.values(ALLOWED_INTERFACES_FOR_CC).includes(
    activeMintSchedule.interfaceId,
  );

  return {
    totalPriceEth,
    isAllowed,
    activeMintSchedule,
  };
}

export const CREDIT_CARD_FEE_INFO_COPY =
  'Includes credit card processing fees and gas fees paid to miners to process transactions securely on the blockchain.';
